import type { ComponentType, ReactNode } from "react";
import { Folder, Globe, ScanSearch, Sparkles, Terminal as TerminalIcon } from "lucide-react";
import type { DockPanel, PanelKind } from "../../stores/dock";
import { artifactKind } from "./kinds/artifact";
import { browserKind } from "./kinds/browser";
import { filesKind } from "./kinds/files";
import { inspectorKind } from "./kinds/inspector";
import { terminalKind } from "./kinds/terminal";

/**
 * The dock's kind registry (2026-07-15) — the ONE seam between the dock host
 * (DockColumn / DockCard, which know about cards, tabs and resizing) and the
 * things a card can contain (a terminal, a browser, a file, an artifact).
 *
 * The contract that makes this worth having: **adding a kind must not touch the
 * host.** A new kind is its own folder plus one line in `DOCK_KINDS` below. If a
 * change to `files` ever forces an edit to DockCard, the seam is in the wrong
 * place — fix the seam, don't special-case the host.
 *
 * It is a plain static map, not a `register()` bus. Self-registration reads
 * flexible but buys nothing here: some module still has to import every kind for
 * its side effect, so the central file exists either way — only now it also has
 * an import-order hazard and no exhaustiveness from the type checker.
 */

export interface DockKindProps {
	/** The panel to render. Kinds read `panel.data` themselves — the host never
	 *  does, which is exactly what keeps `data` opaque and the host kind-blind. */
	panel: DockPanel;
}

export interface DockKindDef {
	/** Tab glyph for one open panel. A function because some kinds derive it from
	 *  the panel (an artifact's glyph follows its language) and others ignore the
	 *  argument entirely (a terminal is always a terminal). */
	tabIcon: (panel: DockPanel) => ReactNode;
	/** The card body. */
	Body: ComponentType<DockKindProps>;
	/** Optional controls for the card's chrome row, left of expand/close — the
	 *  artifact's Save/Copy/Download, a browser's nav arrows. */
	Toolbar?: ComponentType<DockKindProps>;
	/** Mint a fresh panel of this kind, when the kind CAN be conjured.
	 *
	 *  Deliberately optional, and the distinction is real rather than
	 *  bookkeeping: a terminal is a new shell on demand, but an artifact arrives
	 *  from the chat and cannot be created out of nothing. The rail offers "new"
	 *  only where this exists, so it can never present a button that would have
	 *  to lie about what it does. */
	create?: () => DockPanel;
}

/** A kind's IDENTITY — its name and glyph. */
export interface DockKindIdentity {
	label: string;
	railIcon: ReactNode;
}

/**
 * Every kind the dock is being built toward, in rail order — identity ONLY,
 * deliberately separate from `DOCK_KINDS` below.
 *
 * The split is what lets the rail show the whole shape on day one: a kind is
 * named and drawn here BEFORE it has a Body, so the launcher can offer Terminal
 * as a visibly not-yet-built button instead of pretending the concept doesn't
 * exist. Identity is also the half that never depends on a component, so nothing
 * here can drag a panel implementation into the rail's bundle.
 *
 * This is a total Record, not a Partial: forgetting to name a new kind here is a
 * type error, which is exactly the mistake worth catching — an unnamed kind
 * would silently vanish from the launcher.
 */
export const DOCK_KIND_CATALOG: Record<PanelKind, DockKindIdentity> = {
	terminal: { label: "Terminal", railIcon: <TerminalIcon size={16} /> },
	files: { label: "Files", railIcon: <Folder size={16} /> },
	browser: { label: "Browser", railIcon: <Globe size={16} /> },
	artifact: { label: "Artifacts", railIcon: <Sparkles size={16} /> },
	inspector: { label: "Inspector", railIcon: <ScanSearch size={16} /> },
};

/** Rail order — the catalog's declaration order, named once so the rail and any
 *  future menu cannot drift apart. */
export const KIND_ORDER = Object.keys(DOCK_KIND_CATALOG) as PanelKind[];

export type DockKindMap = Partial<Record<PanelKind, DockKindDef>>;

/**
 * The live registry. Deliberately `Partial`: `PanelKind` names every kind the
 * dock is being built toward, and the entries land as each ships. An unregistered
 * kind is a real state, and `DockCard` says so out loud rather than drawing an
 * empty card — a blank panel is indistinguishable from a broken one.
 */
export const DOCK_KINDS: DockKindMap = {
	artifact: artifactKind,
	terminal: terminalKind,
	inspector: inspectorKind,
	files: filesKind,
	browser: browserKind,
};

export function resolveDockKind(kind: PanelKind, kinds: DockKindMap = DOCK_KINDS): DockKindDef | null {
	return kinds[kind] ?? null;
}
